import React from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, AlertTriangle } from "lucide-react";
import { useLang } from "@/i18n";
import { Overline, ProgressBar, Checkbox, cx } from "@/ui";

export function WizardLayout({ overline, gold, step, total, title, subtitle, children }) {
  return (
    <div className="grain min-h-screen pt-32 pb-24">
      <div className="max-w-2xl mx-auto px-6">
        <div className="flex items-center justify-between mb-3">
          <Overline gold={gold}>{overline}</Overline>
          <span className="font-mono text-xs text-white/40">{String(step).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
        </div>
        <ProgressBar step={step} total={total} gold={gold} />
        <div className="mt-10">
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white">{title}</h1>
          {subtitle && <p className="text-white/45 font-light mt-3">{subtitle}</p>}
        </div>
        <div className="mt-10">
          <AnimatePresence mode="wait">{children}</AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export function EmergencyNote() {
  const { L } = useLang();
  return (
    <div className="flex items-start gap-3 rounded-sm border border-[#C7A668]/40 bg-[#C7A668]/[0.06] p-4">
      <AlertTriangle className="text-[#C7A668] shrink-0 mt-0.5" size={18} />
      <p className="text-sm text-[#C7A668]/90 font-light">{L("Emergency Response usually requires Priority or Immediate confirmation.", "La Respuesta de Emergencia suele requerir confirmación Prioritaria o Inmediata.")}</p>
    </div>
  );
}

export function LegalConfirmations({ state, onChange }) {
  const { L } = useLang();
  const items = [
    ["authorized", L("I confirm I am authorized to request service for this aircraft, or act on behalf of an authorized party.", "Confirmo que estoy autorizado para solicitar servicio para esta aeronave, o actúo en nombre de una parte autorizada.")],
    ["terms", L("I agree to the Terms of Service.", "Acepto los Términos de Servicio.")],
    ["nda", L("I agree to the Confidentiality & Non-Disclosure Agreement.", "Acepto el Acuerdo de Confidencialidad y No Divulgación.")],
    ["access", L("I acknowledge service is subject to access and operational confirmation.", "Reconozco que el servicio está sujeto a confirmación de acceso y operación.")],
    ["nomaint", L("I understand Dukk does not perform maintenance, repair, inspection or modification.", "Entiendo que Dukk no realiza mantenimiento, reparación, inspección ni modificación.")],
  ];
  return (
    <div className="space-y-3 rounded-sm border border-white/10 bg-white/[0.02] p-5">
      {items.map(([k, label]) => (
        <Checkbox key={k} testid={`legal-${k}`} checked={!!state[k]} onChange={(v) => onChange((prev) => ({ ...prev, [k]: v }))}>{label}</Checkbox>
      ))}
    </div>
  );
}

export const allLegal = (s) => ["authorized", "terms", "nda", "access", "nomaint"].every((k) => s[k]);

export function ConfirmationScreen({ result, summary }) {
  const { L } = useLang();
  const nav = useNavigate();
  return (
    <motion.div initial={{ opacity: 0, y: 24, filter: "blur(10px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ duration: 0.7 }} className="grain min-h-screen pt-32 pb-24">
      <div className="max-w-xl mx-auto px-6 text-center">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 160 }} className="mx-auto h-16 w-16 rounded-full border border-[#C7A668] flex items-center justify-center shadow-[0_0_24px_rgba(37,99,235,0.5)]">
          <Check className="text-[#C7A668]" size={30} />
        </motion.div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-8">{L("Request received.", "Solicitud recibida.")}</h1>
        <p className="text-[#C7A668] font-mono text-sm tracking-widest uppercase mt-3">{L("Concierge validation pending", "Validación de concierge pendiente")}</p>
        <p className="text-white/50 font-light mt-6 leading-relaxed">
          {L("We've received your aircraft service request. Dukk Concierge will review the aircraft details, validate access with your operational contact and confirm the service window before issuing a confirmed Service Ticket.", "Recibimos tu solicitud. Dukk Concierge revisará los detalles, validará el acceso con tu contacto operativo y confirmará la ventana de servicio antes de emitir un Ticket de Servicio.")}
        </p>
        <div className="mt-8 rounded-sm border border-white/10 bg-white/[0.03] p-6">
          <Overline>{L("Request ID", "ID de Solicitud")}</Overline>
          <p data-testid="confirmation-request-id" className="font-mono text-3xl text-white tracking-wider mt-2">{result.requestId}</p>
          <div className="mt-6 text-left space-y-2 border-t border-white/10 pt-5">
            {summary.map(([k, v]) => v ? (
              <div key={k} className="flex justify-between gap-4 text-sm">
                <span className="text-white/40 font-mono text-xs tracking-wider uppercase">{k}</span>
                <span className="text-white/75 font-light text-right">{v}</span>
              </div>
            ) : null)}
          </div>
        </div>
        <p className="text-white/40 font-light text-sm mt-6">{L("We'll contact your operational contact. Once access, scope and a refundable deposit are confirmed, we'll issue your Service Ticket and tracking link.", "Contactaremos a tu contacto operativo. Una vez confirmados acceso, alcance y un depósito reembolsable, emitiremos tu Ticket de Servicio y enlace de seguimiento.")}</p>
        <div className="mt-9 flex flex-wrap justify-center gap-4">
          <button data-testid="back-home-btn" onClick={() => nav("/")} className="bg-[#C7A668] text-[#050505] px-7 py-3.5 font-semibold tracking-widest uppercase text-sm hover:opacity-90 transition-all" style={{ borderRadius: 999 }}>{L("Back to home", "Volver al inicio")}</button>
          <button data-testid="submit-another-btn" onClick={() => nav(0)} className="border text-white/80 px-7 py-3.5 font-semibold tracking-widest uppercase text-sm hover:border-white/50 transition-all" style={{ borderRadius: 999, borderColor: "rgba(255,255,255,.2)" }}>{L("Submit another request", "Enviar otra solicitud")}</button>
          <button data-testid="track-from-confirm-btn" onClick={() => nav("/track?id=" + result.requestId)} className="text-[#C7A668] px-7 py-3.5 font-semibold tracking-widest uppercase text-sm transition-all" style={{ borderRadius: 999, border: "1px solid rgba(125,175,255,.4)" }}>{L("Track status", "Ver estatus")}</button>
        </div>
      </div>
    </motion.div>
  );
}

export function DepositNote({ cabinReady }) {
  const { L } = useLang();
  return (
    <div className="rounded-sm border border-white/10 bg-white/[0.02] p-5">
      <Overline gold>{L("Refundable deposit", "Depósito reembolsable")}</Overline>
      <p className="text-white/70 font-light mt-3 text-sm leading-relaxed">
        {L("Refundable deposit required after concierge validation. Once access and scope are validated, Dukk Concierge will contact the operational contact with the final quote and refundable deposit instructions. The deposit is applied to the final invoice and is fully refundable if we cannot confirm the service.", "Depósito reembolsable requerido tras la validación del concierge. Una vez validados acceso y alcance, te contactaremos con la cotización final e instrucciones del depósito reembolsable. El depósito se aplica a la factura final y es totalmente reembolsable si no podemos confirmar el servicio.")}
      </p>
      {cabinReady && <p className="text-white/40 text-xs font-light mt-3">{L("A $1,500 MXN refundable deposit may be required after access and scope validation.", "Puede requerirse un depósito reembolsable de $1,500 MXN tras validar acceso y alcance.")}</p>}
    </div>
  );
}
